/**
 * @fileoverview Dollar Liquidity Observatory Manifest
 * Defines metadata and configuration for the Dollar Liquidity Observatory.
 * @module observatories/dollar_liquidity/manifest
 */

/**
 * @typedef {Object} ObservatoryManifest
 * @property {string} id - Unique identifier for the observatory
 * @property {string} name - Display name
 * @property {string} shortDescription - Brief summary (1-2 sentences)
 * @property {string} longDescription - Extended description with worldview context
 * @property {string} category - Classification category
 * @property {string[]} tags - Searchable tags
 * @property {string} version - Semantic version
 * @property {string} defaultTimeRange - Default time range for data display
 * @property {string} heroSignalId - Primary signal identifier
 */

/**
 * Manifest for the Dollar Liquidity Observatory.
 * Tracks global USD funding conditions through DXY, cross-currency basis swaps,
 * and BIS global USD credit metrics.
 * @type {ObservatoryManifest}
 */
export const dollarLiquidityManifest = {
  id: "dollar_liquidity",
  name: "Dollar Liquidity Observatory",
  shortDescription:
    "Monitors global USD funding stress through DXY strength, cross-currency basis swaps, and offshore dollar credit conditions.",
  longDescription:
    "The Dollar Liquidity Observatory tracks the pulse of the global dollar system. " +
    "When the dollar strengthens (rising DXY), it squeezes offshore borrowers who hold USD-denominated debt. " +
    "Cross-currency basis swaps reveal the premium foreign institutions pay to access dollar funding. " +
    "Combined with BIS global USD credit data, this observatory surfaces early warning signs of liquidity stress " +
    "that often precede broader market dislocations. A tightening dollar is the tide going out—revealing who has been swimming naked.",
  category: "liquidity",
  tags: ["dxy", "basis", "usd", "funding", "liquidity", "cross-currency", "bis", "offshore"],
  version: "0.1.0",
  defaultTimeRange: "1Y",
  heroSignalId: "dxy_trend",
};
